// Admin AI usage page: types of the backend report (hand-written, production
// introspection is off) and pure formatting helpers.

export interface AiUsageRow {
  user: { id: string; name: string | null; email: string | null };
  plan_name: string | null;
  is_paid: boolean;
  messages: number;
  model_calls: number;
  prompt_tokens: number;
  completion_tokens: number;
  cached_tokens: number;
  cost_usd: number;
  last_used_at: string | null;
}

export interface AiUsageReport {
  from: string;
  to: string;
  totals: {
    active_users: number; messages: number; model_calls: number; failed_calls: number; unpriced_calls: number;
    prompt_tokens: number; completion_tokens: number; cached_tokens: number; cost_usd: number;
  };
  by_model: Array<{
    llm_model: string; purpose: string; model_calls: number; failed_calls: number; unpriced_calls: number;
    prompt_tokens: number; completion_tokens: number; cost_usd: number;
  }>;
  by_day: Array<{ day: string; messages: number; model_calls: number; cost_usd: number }>;
  rows: AiUsageRow[];
  total_rows: number;
}

/** Same comparison rate autoclipper-analytics uses for USD revenue. */
export const DEFAULT_BRL_RATE = 5;

export const usdToBrl = (usd: number, rate: number) => usd * rate;

export function parseBrlRate(input: string): number {
  const value = Number(String(input).trim().replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? value : DEFAULT_BRL_RATE;
}

export const formatUsd = (value: number, locale: string) => new Intl.NumberFormat(locale, {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: value < 1 ? 4 : 2,
}).format(value);

export const formatBrl = (value: number, locale: string) => new Intl.NumberFormat(locale, {
  style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: value < 1 ? 4 : 2,
}).format(value);

// Names, e-mails and plan names are user-controlled: a leading = + - @ (or a
// tab/CR) would run as a formula in Excel/Sheets, so such text gets a '.
const FORMULA_START = /^[=+\-@\t\r]/;

const csvField = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  const text = typeof value === 'string' && FORMULA_START.test(raw) ? `'${raw}` : raw;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const HEADER = [
  'user_id', 'name', 'email', 'plan', 'paid', 'messages', 'model_calls', 'prompt_tokens', 'completion_tokens',
  'cached_tokens', 'cost_usd', 'cost_brl', 'cost_per_message_brl', 'last_used_at',
];

export function buildAiUsageCsv(rows: readonly AiUsageRow[], { brlRate }: { brlRate: number }): string {
  const lines = rows.map((row) => {
    const brl = usdToBrl(row.cost_usd, brlRate);
    return [
      row.user.id, row.user.name, row.user.email, row.plan_name, row.is_paid ? 'yes' : 'no',
      row.messages, row.model_calls, row.prompt_tokens, row.completion_tokens, row.cached_tokens,
      row.cost_usd.toFixed(6), brl.toFixed(6), row.messages > 0 ? (brl / row.messages).toFixed(6) : null,
      row.last_used_at,
    ].map(csvField).join(',');
  });
  return `${[HEADER.join(','), ...lines].join('\n')}\n`;
}
